import { format } from 'date-fns';
import { FC, ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { MAX_SPACE_FOLDER_DEPTH, SpaceKind } from 'shared';
import { twMerge } from 'tailwind-merge';

import {
  FolderDownloadIcon,
  LockIcon,
  NewFolderIcon,
  SettingsLgIcon,
  SlashCircleIcon,
  SuccessCircleIcon,
  UploadArrowIcon,
  UsersIcon,
} from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button, { ButtonDisableStyle } from '../../../../components/button/index';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components/popover/index';
import { Search } from '../../../../components/search/index';
import { Tooltip } from '../../../../components/tooltip/index';
import { useLocale } from '../../../../hooks/index';
import { WORKSPACE_CARD_STATUS_LABEL } from '../../constants/workspace-card.const';
import {
  SelectedSpace,
  SpaceBrowserPermissions,
  WorkspaceCardStatus,
} from '../../types/data-room.types';
import { spaceCardStatus } from '../../utils/space-list-item.util';

const ToolbarIconButton: FC<{
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
  label: string;
  onClick?: () => void;
  requiresWrite?: boolean;
}> = ({ children, className, disabled, isLoading, label, onClick, requiresWrite }) => (
  <Button
    aria-label={label}
    className={twMerge(
      'size-9 border border-gray-100 bg-white shadow-[0px_1px_2px_rgba(16,24,40,0.05)]',
      className,
    )}
    data-permission-required={requiresWrite || undefined}
    disabled={disabled}
    disabledStyle={ButtonDisableStyle.NONE}
    isLoading={isLoading}
    onClick={onClick}
    styleType="NONE"
  >
    {children}
  </Button>
);

// What an icon button does, or why it is off, on hover. A `div`, not a `span`: the tooltip shows a
// text tag's content only once it is cut off, and a button never is. It also takes the hover a
// disabled button does not.
const ToolbarTooltip: FC<{ children: ReactNode; content: ReactNode }> = ({ children, content }) => (
  <Tooltip childrenWrapperClassName="!inline-flex !overflow-visible" content={content}>
    <div>{children}</div>
  </Tooltip>
);

const MetaSeparator: FC = () => <span aria-hidden className="h-[9px] w-px shrink-0 bg-gray-200" />;

const SpaceDescription: FC<{ description: string }> = ({ description }) => {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const text = textRef.current;

    setIsTruncated(!!text && text.scrollWidth > text.clientWidth);
  }, [description]);

  return (
    <span className="flex min-w-0 items-center gap-2">
      <p className="max-w-[244px] truncate text-gray-500" ref={textRef}>
        {description}
      </p>
      {isTruncated && (
        <Popover>
          <PopoverTrigger className="shrink-0 text-brand-700 underline">
            <AppFormattedMessage id="SEE_ALL" />
          </PopoverTrigger>
          {/* `anywhere` rather than `break-words`: the content is a flex item, and
              `overflow-wrap: break-word` leaves its min-content width the width of the longest
              word — a description typed without spaces then runs straight out of the panel. */}
          <PopoverContent
            align="start"
            className="w-80 text-wrap text-xs font-[450] text-gray-700 [overflow-wrap:anywhere]"
          >
            {description}
          </PopoverContent>
        </Popover>
      )}
    </span>
  );
};

type WorkspaceBrowserHeaderProps = {
  // The open Vault folder takes uploads, by the Vault's own rule; the Vault has no other tools.
  canUploadToVault: boolean;
  description?: null | string;
  /** A zip being packed: the button waits rather than going quiet while the bytes are written. */
  isDownloadingArchive?: boolean;
  // An upload in flight: the button waits for it, so one drop's progress is counted.
  isUploading: boolean;
  onCreateFolder: () => void;
  /** Absent where no route carries an archive: the Vault. */
  onDownloadArchive?: () => void;
  /** Absent where the viewer may not reach the sharing flow: 20.2.4.2 keeps a Guest Auditor out. */
  onManageAccess?: () => void;
  onSearch: (search: string) => void;
  onSettings: () => void;
  onUpload: () => void;
  permissions: SpaceBrowserPermissions;
  // A new key is a new, empty search box: the page changes it on every move.
  searchKey: string;
  space: SelectedSpace;
};

// The open space's name and status, and its toolbar. The Vault has no settings or folders and takes
// uploads only where it allows them; a recipient sees the name, the description and the search, and
// none of the owner's tools.
export const WorkspaceBrowserHeader: FC<WorkspaceBrowserHeaderProps> = ({
  canUploadToVault,
  description,
  isDownloadingArchive = false,
  isUploading,
  onCreateFolder,
  onDownloadArchive,
  onManageAccess,
  onSearch,
  onSettings,
  onUpload,
  permissions: { canCreateFolder, canManage, canWrite, isReadOnly },
  searchKey,
  space,
}) => {
  const { messagesLocale } = useLocale();
  // Inactive while an upload runs, as it is where uploads are not allowed: one upload at a time.
  const isVault = space.kind === SpaceKind.VAULT;
  const canUpload = (isVault ? canUploadToVault : canWrite) && !isUploading;
  // Where the Vault allows files the button is there; elsewhere in it, as before, it is not.
  const showsUpload = isVault ? canUploadToVault : canManage;
  const isDataRoom = space.kind === SpaceKind.DATA_ROOM;
  const status = spaceCardStatus(space);
  const hasOwnerMeta = isDataRoom && canManage && !!status && !!space.createdAt;

  return (
    <div className="flex items-center justify-between gap-4 border-b border-gray-100 px-4 py-4">
      <div className="min-w-0">
        <h2 className="truncate text-base font-semibold leading-8 text-gray-700">{space.name}</h2>
        {(hasOwnerMeta || description) && (
          <div className="flex items-center gap-2 text-label-sm font-medium">
            {hasOwnerMeta && space.createdAt && status && (
              <>
                <p className="shrink-0 text-gray-500">
                  <AppFormattedMessage
                    id="CREATED_ON"
                    values={{ date: format(new Date(space.createdAt), 'dd/MM/yyyy') }}
                  />
                </p>
                <MetaSeparator />
                <span
                  className={twMerge(
                    'flex shrink-0 items-center gap-1',
                    status === WorkspaceCardStatus.ACTIVE && 'text-forest-600',
                    status === WorkspaceCardStatus.INACTIVE && 'text-gray-700',
                    status === WorkspaceCardStatus.LOCKED && 'text-sunbeam-600',
                  )}
                >
                  {status === WorkspaceCardStatus.ACTIVE && (
                    <SuccessCircleIcon iconColor="#039855" />
                  )}
                  {status === WorkspaceCardStatus.INACTIVE && (
                    <SlashCircleIcon
                      className="size-3"
                      height={12}
                      iconColor="#344054"
                      width={12}
                    />
                  )}
                  {status === WorkspaceCardStatus.LOCKED && (
                    <LockIcon className="size-3" height={12} iconColor="#DC6803" width={12} />
                  )}
                  <AppFormattedMessage id={WORKSPACE_CARD_STATUS_LABEL[status]} />
                </span>
                {description && <MetaSeparator />}
              </>
            )}
            {description && <SpaceDescription description={description} />}
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-3">
        <Search buttonClassName="bg-white" callback={onSearch} key={searchKey} />
        {!isVault && canManage && (
          <ToolbarTooltip content={messagesLocale['SETTINGS']}>
            <ToolbarIconButton
              label={messagesLocale['SETTINGS']}
              onClick={onSettings}
              requiresWrite
            >
              <SettingsLgIcon />
            </ToolbarIconButton>
          </ToolbarTooltip>
        )}
        {/* 20.4 §4: the whole space as one zip; not there for the Vault. */}
        {onDownloadArchive && (
          <ToolbarTooltip content={messagesLocale['DOWNLOAD']}>
            <ToolbarIconButton
              isLoading={isDownloadingArchive}
              label={messagesLocale['DOWNLOAD']}
              onClick={onDownloadArchive}
            >
              <FolderDownloadIcon />
            </ToolbarIconButton>
          </ToolbarTooltip>
        )}
        {isDataRoom && canManage && onManageAccess && (
          <Button
            className="h-9 w-fit gap-2 border border-gray-100 bg-white px-3 text-sm font-[450] text-gray-700 shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
            onClick={onManageAccess}
            styleType="NONE"
          >
            <AppFormattedMessage id="MANAGE_ACCESS" />
            <UsersIcon className="size-4" height={16} iconColor="#344054" width={16} />
          </Button>
        )}
        {showsUpload && (
          <ToolbarTooltip
            content={
              canWrite || isVault ? (
                messagesLocale['UPLOAD_FILES']
              ) : (
                <AppFormattedMessage id="DATA_ROOM_INACTIVE_NO_UPLOADS" />
              )
            }
          >
            <ToolbarIconButton
              className="border-brand-100 bg-brand-50 shadow-none disabled:border-gray-200 disabled:bg-gray-100"
              disabled={!canUpload}
              label={messagesLocale['UPLOAD']}
              onClick={onUpload}
              requiresWrite
            >
              <UploadArrowIcon iconColor={canUpload ? '#2565C8' : '#D0D5DD'} />
            </ToolbarIconButton>
          </ToolbarTooltip>
        )}
        {!isVault && canManage && (
          <ToolbarTooltip
            content={
              canCreateFolder ? (
                messagesLocale['CREATE_FOLDER']
              ) : isReadOnly ? (
                <AppFormattedMessage id="DATA_ROOM_INACTIVE_NO_NEW_FOLDERS" />
              ) : (
                <AppFormattedMessage
                  id="FOLDER_DEPTH_REACHED"
                  values={{ max: MAX_SPACE_FOLDER_DEPTH }}
                />
              )
            }
          >
            <ToolbarIconButton
              className="border-brand-700 bg-brand-700 shadow-none disabled:border-gray-200 disabled:bg-gray-100"
              disabled={!canCreateFolder}
              label={messagesLocale['CREATE_FOLDER']}
              onClick={onCreateFolder}
              requiresWrite
            >
              <NewFolderIcon iconColor={canCreateFolder ? '#ffffff' : '#D0D5DD'} />
            </ToolbarIconButton>
          </ToolbarTooltip>
        )}
      </div>
    </div>
  );
};
